import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Grid from "@saleor/components/Grid";
import { MetadataFormData } from "@saleor/components/Metadata";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { ProductErrorWithAttributesFragment } from "@saleor/fragments/types/ProductErrorWithAttributesFragment";
import { ProductVariant } from "@saleor/fragments/types/ProductVariant";
import { WarehouseFragment } from "@saleor/fragments/types/WarehouseFragment";
import { FormsetData } from "@saleor/hooks/useFormset";
import { Brand } from "@saleor/products/customTypes/Brand";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import { ProductVariant_productVariant_stockOnMall } from "@saleor/products/types/ProductVariant";
import { ProductVariantDetails_productVariant_sellingPrice } from "@saleor/products/types/ProductVariantDetails";
import { VariantUpdate_productVariantUpdate_errors } from "@saleor/products/types/VariantUpdate";
import { StaffTenantAssignment_tenant_mall } from "@saleor/staff/types/StaffTenantAssignment";
import { ReorderAction } from "@saleor/types";
import React from "react";

import { maybe } from "../../../misc";
import ProductDimension from "../ProductDimensions";
import ProductKAMSDropdownGroup from "../ProductKAMSDropdownGroup/ProductKAMSDropdownGroup";
// import ProductShipping from "../ProductShipping/ProductShipping";
import ProductStocks, { ProductStockInput } from "../ProductStocks";
import ProductVariantAttributes, {
  VariantAttributeInputData
} from "../ProductVariantAttributes";
import ProductVariantImages from "../ProductVariantImages";
import ProductVariantImageSelectDialog from "../ProductVariantImageSelectDialog";
import ProductVariantNavigation from "../ProductVariantNavigation";
import ProductVariantPrice from "../ProductVariantPrice";
import ProductVariantSetDefault from "../ProductVariantSetDefault";
import ProductVariantUpdateForm from "./form";

export interface ProductVariantPageFormData extends MetadataFormData {
  brand?: string;
  costPrice: string;
  height: string;
  length: string;
  price: string;
  sku: string;
  trackInventory: boolean;
  weight: string;
  width: string;
  stockOnMall: string;
}

export interface ProductVariantPageSubmitData
  extends ProductVariantPageFormData {
  attributes: FormsetData<VariantAttributeInputData, string>;
  addStocks: ProductStockInput[];
  updateStocks: ProductStockInput[];
  removeStocks: string[];
}

interface ProductVariantPageProps {
  brand?: string;
  brands?: Brand[];
  brandLabel?: string;
  onBrandChange?: (e) => void;
  userView?: string;
  userViews?: any;
  userViewLabel?: string;
  onUserViewChange?: (e) => void;
  productsUser?: ProductsUser;
  isStoreAdmin?: boolean;
  isSellerMaster?: boolean;
  defaultVariantId?: string;
  defaultWeightUnit: string;
  errors:
    | ProductErrorWithAttributesFragment[]
    | VariantUpdate_productVariantUpdate_errors[];
  header: string;
  loading?: boolean;
  placeholderImage?: string;
  saveButtonBarState: ConfirmButtonTransitionState;
  tenant: StaffTenantAssignment_tenant_mall;
  variant?: ProductVariant;
  variantyQuantity?: ProductVariant_productVariant_stockOnMall;
  absoluteValue?: ProductVariantDetails_productVariant_sellingPrice;
  warehouses: WarehouseFragment[];
  validate: {
    price: null;
    height: null;
    length: null;
    sku: null;
    weight: null;
    width: null;
  };
  onVariantReorder: ReorderAction;
  onAdd();
  onBack();
  onDelete();
  onSubmit(data: ProductVariantPageSubmitData);
  onImageSelect(id: string);
  onVariantClick(variantId: string);
  onSetDefaultVariant();
  onWarehouseConfigure();
}

const ProductVariantPage: React.FC<ProductVariantPageProps> = ({
  defaultVariantId,
  errors,
  header,
  loading,
  placeholderImage,
  saveButtonBarState,
  tenant,
  variant,
  variantyQuantity,
  absoluteValue,
  warehouses,
  validate,
  onAdd,
  onBack,
  onDelete,
  onImageSelect,
  onSubmit,
  onVariantClick,
  onVariantReorder,
  onSetDefaultVariant,
  onWarehouseConfigure,
  isStoreAdmin,
  isSellerMaster,
  brand,
  brands,
  brandLabel,
  onBrandChange,
  userView,
  userViews,
  userViewLabel,
  onUserViewChange,
  productsUser
}) => {
  const [isModalOpened, setModalStatus] = React.useState(false);
  const toggleModal = () => setModalStatus(!isModalOpened);

  const variantImages = variant?.images?.map(image => image.id);
  const productImages = variant?.product?.images?.sort((prev, next) =>
    prev.sortOrder > next.sortOrder ? 1 : -1
  );
  const images = productImages
    ?.filter(image => variantImages.indexOf(image.id) !== -1)
    .sort((prev, next) => (prev.sortOrder > next.sortOrder ? 1 : -1));

  const getCurrencySymbol = (): string =>
    (0)
      .toLocaleString("en-US", {
        currency: "PHP",
        currencyDisplay: "symbol",
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: "currency"
      })
      .replace(/\d/g, "")
      .trim();

  const currencySymbol = getCurrencySymbol();
  return (
    <>
      <Container>
        <AppHeader onBack={onBack} idAppHeader="productVariantBackBtn">
          {maybe(() => variant.product.name)}
        </AppHeader>
        {productsUser.isKAMS && (
          <ProductKAMSDropdownGroup
            brand={brand}
            brandChoices={brands}
            brandLabel={brandLabel}
            brandDisabled={true}
            onBrandChange={onBrandChange}
            userView={userView}
            userViewChoices={userViews}
            userViewLabel={userViewLabel}
            onUserViewChange={onUserViewChange}
          />
        )}
        <PageHeader title={header}>
          {isStoreAdmin
            ? null
            : variant?.product?.defaultVariant?.id !== variant?.id && (
                <ProductVariantSetDefault
                  onSetDefaultVariant={onSetDefaultVariant}
                />
              )}
        </PageHeader>
        <ProductVariantUpdateForm
          variant={variant}
          variantQuantity={variantyQuantity}
          absoluteValue={absoluteValue}
          onSubmit={onSubmit}
          warehouses={warehouses}
        >
          {({ change, data, handlers, hasChanged, submit }) => (
            <>
              <Grid variant="inverted">
                <div>
                  <ProductVariantNavigation
                    current={variant ? variant.id : undefined}
                    defaultVariantId={defaultVariantId}
                    fallbackThumbnail={maybe(
                      () => variant.product.thumbnail.url
                    )}
                    variants={maybe(() => variant.product.variants)}
                    onAdd={onAdd}
                    onRowClick={(variantId: string) => {
                      if (variant) {
                        return onVariantClick(variantId);
                      }
                    }}
                    onReorder={onVariantReorder}
                    isStoreAdmin={isStoreAdmin}
                  />
                </div>
                <div>
                  <ProductVariantAttributes
                    attributes={data.attributes}
                    disabled={loading}
                    errors={errors}
                    onChange={handlers.selectAttribute}
                    isStoreAdmin={isStoreAdmin}
                  />
                  <CardSpacer />
                  <ProductVariantImages
                    disabled={loading}
                    images={images}
                    placeholderImage={placeholderImage}
                    onImageAdd={toggleModal}
                    isStoreAdmin={isStoreAdmin}
                  />
                  <CardSpacer />
                  <ProductDimension
                    data={data}
                    title="Dimension"
                    isStoreAdmin={isStoreAdmin}
                    onFormDataChange={change}
                    validate={validate}
                  />
                  <CardSpacer />
                  <ProductVariantPrice
                    data={data}
                    errors={errors}
                    currencySymbol={currencySymbol}
                    loading={loading}
                    onChange={change}
                    isStoreAdmin={isStoreAdmin}
                    isSellerMaster={isSellerMaster}
                    validate={validate}
                  />
                  <CardSpacer />
                  {/* <ProductShipping
                    data={data}
                    disabled={loading}
                    errors={errors}
                    weightUnit={variant?.weight?.unit || defaultWeightUnit}
                    onChange={change}
                  /> */}
                  <CardSpacer />
                  <ProductStocks
                    data={data}
                    disabled={loading}
                    hasVariants={true}
                    errors={errors}
                    stocks={data.stocks}
                    mall={tenant}
                    warehouses={warehouses}
                    onChange={handlers.changeStock}
                    onFormDataChange={change}
                    onWarehouseStockAdd={handlers.addStock}
                    onWarehouseStockDelete={handlers.deleteStock}
                    onWarehouseConfigure={onWarehouseConfigure}
                    isStoreAdmin={isStoreAdmin}
                    validate={validate}
                  />
                  <CardSpacer />
                </div>
              </Grid>
              {/* {isStoreAdmin ? null : ( */}
              <SaveButtonBar
                disabled={loading || !hasChanged}
                state={saveButtonBarState}
                onCancel={onBack}
                onDelete={onDelete}
                onSave={submit}
              />
              {/* // )} */}
            </>
          )}
        </ProductVariantUpdateForm>
      </Container>
      {variant && (
        <ProductVariantImageSelectDialog
          onClose={toggleModal}
          onImageSelect={onImageSelect}
          open={isModalOpened}
          images={productImages}
          selectedImages={maybe(() => variant.images.map(image => image.id))}
        />
      )}
    </>
  );
};
ProductVariantPage.displayName = "ProductVariantPage";
export default ProductVariantPage;
